/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FieldElementProps } from '@/auth/types';
import { Field } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { classes } from '@adobe-commerce/elsie/lib';
import { memo } from 'preact/compat';
import { extractErrorKeyAndMessage } from '@/auth/lib/extractErrorKeyAndMessage';
import { VNode } from 'preact';

export const FieldElement = memo(
  ({
    item,
    errorConfig,
    className,
    itemClassName,
    loading,
    children,
  }: FieldElementProps) => {
    const { errorKey, defaultErrorMessage } =
      extractErrorKeyAndMessage(errorConfig);
    const customErrorMessage = useText(
      `Auth.FormText.${errorKey}.${item.code}`
    )[item.code];

    let errorMessage = '';

    // This check indicates that validation identified an error
    if (defaultErrorMessage.length) {
      errorMessage = customErrorMessage || defaultErrorMessage;
    }

    return (
      <Field
        key={item.id}
        error={errorMessage}
        className={classes([
          itemClassName,
          `${itemClassName}--${item.id}`,
          [`${itemClassName}--${item.id}-hidden`, item.isHidden],
          item.className,
        ])}
        data-testid={`${className}--${item.id}`}
        disabled={loading || item.disabled}
      >
        {children as VNode<{}>}
      </Field>
    );
  }
);
